import { useState, useEffect } from "react";
import { Plus, Pencil, Trash2, Eye, EyeOff, GripVertical, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { api } from "@/lib/api";

const FOOTER_SECTIONS = [
  { value: "policies", label: "Policies" },
  { value: "help", label: "Help" },
  { value: "brand", label: "Brand" },
];

export default function AdminPages() {
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPage, setEditingPage] = useState(null);
  const [form, setForm] = useState({
    title: "",
    content: "",
    metaTitle: "",
    metaDescription: "",
    isActive: true,
    showInFooter: true,
    footerSection: "policies",
    order: 0,
  });

  useEffect(() => {
    loadPages();
  }, []);

  async function loadPages() {
    try {
      const res = await api.get("/pages?all=true");
      setPages(res.data);
    } catch (err) {
      toast.error("Failed to load pages");
    } finally {
      setLoading(false);
    }
  }

  function openCreate() {
    setEditingPage(null);
    setForm({
      title: "",
      content: "",
      metaTitle: "",
      metaDescription: "",
      isActive: true,
      showInFooter: true,
      footerSection: "policies",
      order: pages.length,
    });
    setDialogOpen(true);
  }

  function openEdit(page) {
    setEditingPage(page);
    setForm({
      title: page.title,
      content: page.content,
      metaTitle: page.metaTitle || "",
      metaDescription: page.metaDescription || "",
      isActive: page.isActive,
      showInFooter: page.showInFooter,
      footerSection: page.footerSection || "policies",
      order: page.order || 0,
    });
    setDialogOpen(true);
  }

  async function handleSave() {
    if (!form.title.trim()) {
      toast.error("Title is required");
      return;
    }

    try {
      if (editingPage) {
        await api.put(`/pages/${editingPage.id}`, form);
        toast.success("Page updated");
      } else {
        await api.post("/pages", form);
        toast.success("Page created");
      }
      setDialogOpen(false);
      loadPages();
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to save page");
    }
  }

  async function handleDelete(page) {
    if (!confirm(`Delete "${page.title}"?`)) return;
    try {
      await api.delete(`/pages/${page.id}`);
      toast.success("Page deleted");
      loadPages();
    } catch (err) {
      toast.error("Failed to delete page");
    }
  }

  async function toggleActive(page) {
    try {
      await api.put(`/pages/${page.id}`, { isActive: !page.isActive });
      loadPages();
    } catch (err) {
      toast.error("Failed to update page");
    }
  }

  if (loading) {
    return (
      <div className="p-8">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-secondary rounded w-48" />
          <div className="h-64 bg-secondary rounded" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Content Pages</h1>
          <p className="text-muted-foreground text-sm mt-1">
            Manage policy pages, contact info, and other content
          </p>
        </div>
        <Button onClick={openCreate}>
          <Plus className="w-4 h-4 mr-2" /> Add Page
        </Button>
      </div>

      <div className="bg-card rounded-xl border border-border overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="border-b border-border bg-secondary/30">
              <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Title
              </th>
              <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Slug
              </th>
              <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Footer Section
              </th>
              <th className="text-center px-4 py-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Status
              </th>
              <th className="text-right px-4 py-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {pages.map((page) => (
              <tr key={page.id} className="hover:bg-secondary/20 transition-colors">
                <td className="px-4 py-3">
                  <div className="font-medium">{page.title}</div>
                  {!page.showInFooter && (
                    <span className="text-xs text-muted-foreground">Hidden from footer</span>
                  )}
                </td>
                <td className="px-4 py-3 text-sm text-muted-foreground">
                  /policies/{page.slug}
                </td>
                <td className="px-4 py-3 text-sm">
                  <span className="capitalize">{page.footerSection}</span>
                </td>
                <td className="px-4 py-3 text-center">
                  <button
                    onClick={() => toggleActive(page)}
                    className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${
                      page.isActive
                        ? "bg-green-500/10 text-green-600 hover:bg-green-500/20"
                        : "bg-red-500/10 text-red-600 hover:bg-red-500/20"
                    }`}
                  >
                    {page.isActive ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
                    {page.isActive ? "Active" : "Hidden"}
                  </button>
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center justify-end gap-2">
                    <a
                      href={`/policies/${page.slug}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-2 hover:bg-secondary rounded-lg transition-colors"
                      title="View page"
                    >
                      <ExternalLink className="w-4 h-4 text-muted-foreground" />
                    </a>
                    <button
                      onClick={() => openEdit(page)}
                      className="p-2 hover:bg-secondary rounded-lg transition-colors"
                      title="Edit"
                    >
                      <Pencil className="w-4 h-4 text-muted-foreground" />
                    </button>
                    <button
                      onClick={() => handleDelete(page)}
                      className="p-2 hover:bg-secondary rounded-lg transition-colors text-red-500"
                      title="Delete"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {pages.length === 0 && (
              <tr>
                <td colSpan={5} className="px-4 py-12 text-center text-muted-foreground">
                  No pages yet. Click "Add Page" to create one.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingPage ? "Edit Page" : "Create Page"}</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Title *</Label>
                <Input
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  placeholder="e.g., Contact Information"
                />
              </div>
              <div className="space-y-2">
                <Label>Footer Section</Label>
                <Select
                  value={form.footerSection}
                  onValueChange={(v) => setForm({ ...form, footerSection: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {FOOTER_SECTIONS.map((s) => (
                      <SelectItem key={s.value} value={s.value}>
                        {s.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Content (HTML supported)</Label>
              <Textarea
                value={form.content}
                onChange={(e) => setForm({ ...form, content: e.target.value })}
                placeholder="Page content... HTML tags are supported"
                rows={12}
                className="font-mono text-sm"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Meta Title (SEO)</Label>
                <Input
                  value={form.metaTitle}
                  onChange={(e) => setForm({ ...form, metaTitle: e.target.value })}
                  placeholder="Page title for search engines"
                />
              </div>
              <div className="space-y-2">
                <Label>Display Order</Label>
                <Input
                  type="number"
                  value={form.order}
                  onChange={(e) => setForm({ ...form, order: parseInt(e.target.value) || 0 })}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Meta Description (SEO)</Label>
              <Textarea
                value={form.metaDescription}
                onChange={(e) => setForm({ ...form, metaDescription: e.target.value })}
                placeholder="Brief description for search engines"
                rows={2}
              />
            </div>

            <div className="flex items-center gap-6 pt-2">
              <div className="flex items-center gap-3">
                <Switch
                  checked={form.isActive}
                  onCheckedChange={(v) => setForm({ ...form, isActive: v })}
                />
                <Label className="cursor-pointer">Active (visible to public)</Label>
              </div>
              <div className="flex items-center gap-3">
                <Switch
                  checked={form.showInFooter}
                  onCheckedChange={(v) => setForm({ ...form, showInFooter: v })}
                />
                <Label className="cursor-pointer">Show in footer</Label>
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button variant="outline" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleSave}>
                {editingPage ? "Save Changes" : "Create Page"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
